import { useCallback, useEffect, useRef, useState } from 'react';
import { errText } from '@/components/invoices/invoiceFormat';

const DEAD = [404, 409, 410];

// The proposal page's state: the view, the client's ticks, live prices for them (`quote`), and the submit.
// `api` = { load(), quote(chosen), submit(body) | null (preview) }.
export default function useProposal(api) {
  const [st, setSt] = useState({ phase: 'loading' });
  const [chosen, setChosen] = useState([]);
  const [priced, setPriced] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState(null);
  const ref = useRef(api);
  ref.current = api;
  const seq = useRef(0);
  const take = (v) => { setSt({ phase: 'ok', v }); setChosen(v.chosen); setPriced({ services: v.services, totals: v.totals, terms: v.terms }); };
  const load = useCallback(async () => {
    setSt({ phase: 'loading' });
    setNote(null);
    try { take(await ref.current.load()); } catch (e) {
      setSt({ phase: 'dead', retry: !DEAD.includes(e?.response?.status), message: e?.response?.status === 409 ? errText(e, '') : '' });
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = (id) => {
    if (st.v?.can_pick === false) return;  // view only: nothing to tick
    const next = chosen.includes(id) ? chosen.filter((x) => x !== id) : [...chosen, id];
    setChosen(next);
    setNote(null);
    const n = ++seq.current;
    setQuoting(true);
    ref.current.quote(next).then((r) => { if (n === seq.current) setPriced(r); })
      .catch((e) => { if (n === seq.current) setNote({ tone: 'error', text: errText(e, "Couldn't update the total. Check your connection and try again.") }); })
      .finally(() => { if (n === seq.current) setQuoting(false); });
  };
  const submit = async (name) => {
    const v = st.v;
    const optional = v.services.filter((s) => s.optional).map((s) => s.key);
    setSending(true);
    setNote(null);
    try {
      take(await ref.current.submit({ version: v.version, chosen: optional.filter((k) => chosen.includes(k)), declined: optional.filter((k) => !chosen.includes(k)), name }));
      setNote({ tone: 'ok', text: 'sent' });
    } catch (e) {
      if (e?.response?.status === 410) setSt({ phase: 'dead' });
      else setNote({ tone: 'error', text: errText(e, "Couldn't send your picks. Check your connection and try again."), reload: e?.response?.status === 409 });
    } finally { setSending(false); }
  };
  return { st, chosen, priced, quoting, sending, note, toggle, submit, load };
}
